import React, { useState, useEffect } from "react";
import { getTemplate } from "@/lib/templates";
import { formatDate } from "@/lib/format";
import { Heart, MailOpen, ExternalLink, CalendarDays, Instagram } from "lucide-react";
import TemplateDecor from "@/components/TemplateDecor";
import { getBackgroundUrl } from "@/lib/backgrounds";
import { defaultDesign, mergeDesign } from "@/lib/design";
import { getPremiumTheme, DecorDivider } from "@/lib/premiumThemes";

const pick = (uploaded, url) => uploaded || url || "";

function useCountdown(dateStr) {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0, done: false });
  useEffect(() => {
    if (!dateStr) return;
    const target = new Date(dateStr + "T00:00:00").getTime();
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) { setT({ d: 0, h: 0, m: 0, s: 0, done: true }); return; }
      setT({ d: Math.floor(diff / 86400000), h: Math.floor((diff % 86400000) / 3600000), m: Math.floor((diff % 3600000) / 60000), s: Math.floor((diff % 60000) / 1000), done: false });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [dateStr]);
  return t;
}

function PUnit({ value, label, accent, font }) {
  return (
    <div className="flex flex-col items-center">
      <div className="min-w-[38px] rounded-lg border bg-white/5 px-1.5 py-2 backdrop-blur-md" style={{ borderColor: `${accent}55` }}>
        <span className="text-xl font-light tabular-nums" style={{ fontFamily: font }}>{String(value).padStart(2, "0")}</span>
      </div>
      <span className="mt-1 text-[7px] uppercase tracking-[0.2em] text-white/60">{label}</span>
    </div>
  );
}

function PhotoBlock({ src, shape }) {
  if (!src) return null;
  if (shape === "circle") return <img src={src} alt="" className="mx-auto h-24 w-24 rounded-full border-2 border-white/30 object-cover shadow-lg" />;
  return <img src={src} alt="" className="mx-auto h-32 w-56 max-w-full rounded-xl border border-white/15 object-cover shadow-lg" />;
}

function MiniPerson({ data, accent, font, label }) {
  const src = pick(data.foto, data.foto_url);
  if (!data.nama_lengkap && !data.bio && !src) return null;
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-3 backdrop-blur-md">
      {src && <img src={src} alt="" className="mx-auto h-14 w-14 rounded-full border border-white/25 object-cover" />}
      <p className="mt-1.5 text-[8px] uppercase tracking-[0.25em] text-white/60">{label}</p>
      {data.nama_lengkap && <p className="mt-0.5 text-sm" style={{ fontFamily: font, color: accent }}>{data.nama_lengkap}</p>}
      {(data.ayah || data.ibu) && <p className="mt-1 text-[8px] text-white/60">Putra/Putri {data.ayah && `Bpk. ${data.ayah}`}{data.ayah && data.ibu && " & "}{data.ibu && `Ibu ${data.ibu}`}</p>}
      {data.instagram && <p className="mt-1 inline-flex items-center gap-0.5 text-[8px]" style={{ color: accent }}><Instagram className="h-2.5 w-2.5" />@{data.instagram}</p>}
    </div>
  );
}

export default function InvitationPreview({ s, events = [], gallery = [], design: designProp, pasanganInfo, weddingDate, guestName }) {
  const design = mergeDesign(designProp || {});
  const [opened, setOpened] = useState(true);
  const activeKind = s.active_kind || (s.premium_template ? "premium" : "standard");

  // Common data
  const nama = pasanganInfo?.nama || "Nama Pasangan";
  const namaPria = design.cover.nama_pria;
  const namaWanita = design.cover.nama_wanita;
  const titleNames = (namaPria && namaWanita) ? `${namaPria} & ${namaWanita}` : nama;
  const cd = useCountdown(weddingDate);
  const evs = (events && events.length ? events : [{}]);
  const coverPhoto = pick(design.cover.photo, design.cover.photo_url) || s.couple_photo;
  const showTo = guestName || design.cover.default_to;

  /* -----------------------------------------------
     PREMIUM preview (mini phone-frame) — mirrors
     InvitationPremium visuals in compact form.
     ----------------------------------------------- */
  if (activeKind === "premium") {
    const theme = getPremiumTheme(s.premium_template || "editorial-bw");
    const PAL = theme.palette;
    return (
      <div data-testid="live-preview" data-preview-kind="premium">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Preview Langsung</p>
          <div className="flex rounded-full border border-border p-0.5 text-[11px]">
            <button data-testid="preview-tab-amplop" onClick={() => setOpened(false)}
              className={`rounded-full px-3 py-1 transition-colors ${!opened ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Amplop</button>
            <button data-testid="preview-tab-isi" onClick={() => setOpened(true)}
              className={`rounded-full px-3 py-1 transition-colors ${opened ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Isi</button>
          </div>
        </div>
        <p className="mb-2 text-[10px] uppercase tracking-[0.3em] text-primary" data-testid="preview-active-template">
          Template Aktif: {theme.name}
        </p>
        <div className="mx-auto w-full max-w-[300px] overflow-hidden rounded-[2rem] border-8 border-neutral-800 shadow-2xl" style={{ background: PAL.bg }}>
          <div className="relative h-[540px] overflow-y-auto" style={{ background: PAL.bg, color: PAL.cream, fontFamily: theme.fonts.serif }}>
            {!opened ? (
              // Envelope / cover
              <div className="relative flex h-full flex-col items-center justify-center px-4 text-center">
                {coverPhoto && <img src={coverPhoto} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ opacity: theme.hero === "editorial-overlay" ? 0.85 : 0.55, filter: theme.hero === "editorial-overlay" ? "grayscale(1) contrast(1.05)" : "none" }} />}
                <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, ${PAL.bg}80, ${PAL.bg}c0, ${PAL.bg}f0)` }} />
                <div className="relative z-10">
                  <p className="text-[8px] uppercase tracking-[0.5em]" style={{ color: PAL.accent }}>{design.cover.eyebrow_top || "The Wedding Of"}</p>
                  <DecorDivider theme={theme} w={120} />
                  <h1 className="text-2xl leading-tight" style={{ fontFamily: theme.hero === "minimal-contemporary" || theme.hero === "editorial-overlay" ? theme.fonts.serif : theme.fonts.script, color: PAL.cream }}>
                    {titleNames}
                  </h1>
                  {weddingDate && <p className="mt-3 text-[9px] uppercase tracking-[0.35em]" style={{ color: PAL.dim }}>{formatDate(weddingDate)}</p>}
                  {showTo && (
                    <div className="mt-4 inline-block rounded-full border px-3 py-1" style={{ borderColor: `${PAL.accent}66` }}>
                      <span className="text-[7px] uppercase tracking-[0.3em]" style={{ color: PAL.dim }}>Kepada Yth.</span>
                      <p className="text-[11px]" style={{ fontFamily: theme.fonts.script, color: PAL.accent }}>{showTo}</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Content stack (compact)
              <div className="relative">
                {coverPhoto && (
                  <div className="relative h-40 w-full">
                    <img src={coverPhoto} alt="" className="h-full w-full object-cover" style={{ filter: theme.hero === "editorial-overlay" ? "grayscale(1) contrast(1.05)" : "none" }} />
                    <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, transparent 40%, ${PAL.bg} 100%)` }} />
                  </div>
                )}
                <div className="px-4 pb-6 pt-4 text-center">
                  <p className="text-[8px] uppercase tracking-[0.4em]" style={{ color: PAL.accent }}>{design.cover.eyebrow_top || "The Wedding Of"}</p>
                  <h1 className="mt-2 text-2xl" style={{ fontFamily: theme.fonts.script, color: PAL.cream }}>{titleNames}</h1>
                  {weddingDate && <p className="mt-2 text-[9px] uppercase tracking-[0.3em]" style={{ color: PAL.dim }}>{formatDate(weddingDate)}</p>}
                  <DecorDivider theme={theme} w={120} />
                  {(design.pria.nama_lengkap || design.wanita.nama_lengkap) && (
                    <div className="mt-2 grid grid-cols-2 gap-2 text-[10px]">
                      {[design.pria, design.wanita].map((p, i) => (
                        <div key={i}>
                          {(p.foto || p.foto_url) && (
                            <div className="mx-auto mb-1 h-12 w-12 overflow-hidden rounded-full" style={{ boxShadow: `0 0 0 1px ${PAL.accent}` }}>
                              <img src={p.foto || p.foto_url} alt="" className="h-full w-full object-cover" />
                            </div>
                          )}
                          <p className="italic" style={{ fontFamily: theme.fonts.script, color: PAL.cream }}>{p.nama_lengkap || (i === 0 ? "Pria" : "Wanita")}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {weddingDate && (
                    <div className="mt-4 grid grid-cols-4 gap-1">
                      {["Hari", "Jam", "Menit", "Detik"].map((lbl, i) => {
                        const v = [cd.d, cd.h, cd.m, cd.s][i];
                        return (
                          <div key={lbl} className="rounded border p-1" style={{ borderColor: `${PAL.accent}44` }}>
                            <div className="text-sm tabular-nums" style={{ color: PAL.cream }}>{String(v).padStart(2, "0")}</div>
                            <div className="text-[7px] uppercase tracking-[0.3em]" style={{ color: PAL.dim }}>{lbl}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {evs[0] && (evs[0].nama || evs[0].tempat) && (
                    <div className="mt-4 rounded-lg border p-3 text-left" style={{ borderColor: `${PAL.accent}44` }}>
                      <p className="text-[10px] italic" style={{ fontFamily: theme.fonts.serif, color: PAL.cream }}>{evs[0].nama || "Acara"}</p>
                      {evs[0].tempat && <p className="mt-1 text-[9px]" style={{ color: PAL.dim }}>{evs[0].tempat}</p>}
                      {evs[0].tanggal && <p className="text-[9px]" style={{ color: PAL.dim }}>{formatDate(evs[0].tanggal)} {evs[0].jam && `· ${evs[0].jam}`}</p>}
                    </div>
                  )}
                  {gallery.length > 0 && (
                    <div className="mt-4 grid grid-cols-3 gap-1">
                      {gallery.slice(0, 6).map((g, i) => <img key={i} src={g} alt="" className="h-14 w-full object-cover" />)}
                    </div>
                  )}
                  <p className="mt-4 text-[9px] uppercase tracking-[0.3em] flex items-center justify-center gap-1" style={{ color: PAL.dim }}>
                    <Heart className="h-2.5 w-2.5" style={{ color: PAL.accent, fill: PAL.accent }} /> Wedifylab
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* -----------------------------------------------
     STANDARD preview (existing behavior below)
     ----------------------------------------------- */
  const tpl = getTemplate(s.template || "1");
  const accent = tpl.accent;
  const font = tpl.font;
  const layout = s.photo_layout || "background";
  const bgImage = (layout === "background" && s.couple_photo) ? s.couple_photo : (getBackgroundUrl(s.bg_theme) || null);
  const bgOpacity = Math.max(0.05, Math.min(0.95, (design.bg?.opacity ?? 40) / 100));

  return (
    <div data-testid="live-preview" data-preview-kind="standard">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Preview Langsung</p>
        <div className="flex rounded-full border border-border p-0.5 text-[11px]">
          <button data-testid="preview-tab-amplop" onClick={() => setOpened(false)}
            className={`rounded-full px-3 py-1 transition-colors ${!opened ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Amplop</button>
          <button data-testid="preview-tab-isi" onClick={() => setOpened(true)}
            className={`rounded-full px-3 py-1 transition-colors ${opened ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Isi</button>
        </div>
      </div>
      <p className="mb-2 text-[10px] uppercase tracking-[0.3em] text-primary" data-testid="preview-active-template">
        Template Aktif: Standar #{s.template || "1"}
      </p>

      <div className="mx-auto w-full max-w-[300px] overflow-hidden rounded-[2rem] border-8 border-neutral-800 bg-black shadow-2xl">
        <div className="relative h-[540px] overflow-y-auto text-white" style={{ background: tpl.bg || tpl.base }}>
          {!opened ? (
            <div className="relative flex h-full flex-col items-center justify-center px-5 text-center" style={{ background: tpl.bg || tpl.base }}>
              {(coverPhoto || bgImage) && <img src={coverPhoto || bgImage} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ opacity: bgOpacity * 0.75 }} />}
              <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, ${tpl.base}c0, ${tpl.base}f0)` }} />
              <TemplateDecor cat={tpl.cat} accent={accent} compact />
              <div className="relative z-10">
                <MailOpen className="mx-auto h-8 w-8" style={{ color: accent }} />
                <p className="mt-4 text-[8px] uppercase tracking-[0.4em] text-white/60">{design.cover.eyebrow_top || "The Wedding Of"}</p>
                <h1 className="mt-2 text-2xl font-light tracking-tight" style={{ fontFamily: font }}>{titleNames}</h1>
                {design.cover.eyebrow_bottom && <p className="mt-1 text-[8px] uppercase tracking-[0.3em] text-white/70">{design.cover.eyebrow_bottom}</p>}
                {design.cover.subline && <p className="mt-1 text-[9px] text-white/70">{design.cover.subline}</p>}
                {showTo && <p className="mt-2 text-[10px] text-white/70">Kepada Yth. <span className="font-medium">{showTo}</span></p>}
                <button onClick={() => setOpened(true)} style={{ backgroundColor: accent }}
                  className="mt-5 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[10px] font-semibold text-black">
                  <MailOpen className="h-3 w-3" /> {design.cover.button_label || "Buka Undangan"}
                </button>
              </div>
            </div>
          ) : (
            <div className="relative">
              {bgImage ? (
                <img src={bgImage} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ opacity: bgOpacity }} />
              ) : (
                <div className="absolute inset-0" style={{ background: tpl.bg || `radial-gradient(60% 50% at 50% 20%, ${accent}22 0%, transparent 70%)` }} />
              )}
              <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, ${tpl.base}b0, ${tpl.base}70, ${tpl.base}f0)` }} />
              <TemplateDecor cat={tpl.cat} accent={accent} compact />

              <div className="relative z-10 flex flex-col items-center px-4 py-8 text-center">
                {showTo && (
                  <div className="mb-3 rounded-full border border-white/15 bg-white/5 px-3 py-1 backdrop-blur-md">
                    <span className="text-[8px] uppercase tracking-[0.2em] text-white/60">Kepada Yth.</span>
                    <span className="ml-1 text-[10px] font-medium">{showTo}</span>
                  </div>
                )}

                {/* PEMBUKA */}
                {design.pembuka.enabled && (design.pembuka.text || design.pembuka.quote || design.pembuka.intro) && (
                  <div className="mb-3 w-full rounded-lg border border-white/10 bg-white/5 p-2.5 backdrop-blur-md">
                    {design.pembuka.text && <p className="text-[7px] uppercase tracking-[0.3em] text-white/60">{design.pembuka.text}</p>}
                    {design.pembuka.quote && <p className="mt-1 text-[10px] italic text-white/85" style={{ fontFamily: font }}>"{design.pembuka.quote}"</p>}
                    {design.pembuka.quote_source && <p className="mt-0.5 text-[7px]" style={{ color: accent }}>— {design.pembuka.quote_source}</p>}
                    {design.pembuka.intro && <p className="mt-1.5 text-[9px] text-white/75">{design.pembuka.intro}</p>}
                  </div>
                )}

                <p className="text-[8px] uppercase tracking-[0.4em] text-white/60">{design.cover.eyebrow_top || "The Wedding Of"}</p>

                {(layout === "top" || layout === "circle") && s.couple_photo && <div className="mt-3"><PhotoBlock src={s.couple_photo} shape={layout} /></div>}

                <div className="mt-3 flex items-center justify-center">
                  <Heart className="h-5 w-5" style={{ color: accent, fill: accent }} />
                </div>
                <h1 className="mt-2 text-3xl font-light leading-tight tracking-tight" style={{ fontFamily: font }}>{titleNames}</h1>
                {weddingDate && <p className="mt-2 text-[9px] uppercase tracking-[0.2em] text-white/70">{formatDate(weddingDate)}</p>}

                {s.greeting && (
                  <p className="mt-4 max-w-[240px] text-sm font-light italic leading-relaxed text-white/80" style={{ fontFamily: font }}>"{s.greeting}"</p>
                )}

                {/* MEMPELAI */}
                {(design.pria.nama_lengkap || design.wanita.nama_lengkap || pick(design.pria.foto, design.pria.foto_url) || pick(design.wanita.foto, design.wanita.foto_url)) && (
                  <div className="mt-4 grid w-full grid-cols-2 gap-2">
                    <MiniPerson data={design.pria} accent={accent} font={font} label="Mempelai Pria" />
                    <MiniPerson data={design.wanita} accent={accent} font={font} label="Mempelai Wanita" />
                  </div>
                )}

                {/* COUNTDOWN */}
                {design.countdown.enabled && (
                  <div className="mt-5 flex flex-col items-center gap-1.5">
                    {design.countdown.label_atas && <p className="text-[7px] uppercase tracking-[0.25em] text-white/60">{design.countdown.label_atas}</p>}
                    <div className="flex items-center gap-1.5">
                      {weddingDate ? (
                        cd.done ? <p className="text-base" style={{ fontFamily: font }}>Hari yang dinanti telah tiba!</p> : (
                          <>
                            <PUnit value={cd.d} label="Hari" accent={accent} font={font} />
                            <PUnit value={cd.h} label="Jam" accent={accent} font={font} />
                            <PUnit value={cd.m} label="Menit" accent={accent} font={font} />
                            <PUnit value={cd.s} label="Detik" accent={accent} font={font} />
                          </>
                        )
                      ) : <p className="text-[10px] text-white/60">Tanggal akan diumumkan</p>}
                    </div>
                  </div>
                )}

                {/* Events */}
                <div className="mt-6 w-full space-y-3">
                  {evs.map((ev, i) => (
                    (ev.nama || ev.tempat || ev.tanggal) ? (
                      <div key={i} className="w-full rounded-xl border border-white/10 bg-white/5 p-3 backdrop-blur-md">
                        <CalendarDays className="mx-auto h-3.5 w-3.5" style={{ color: accent }} />
                        {ev.nama && <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.2em]" style={{ color: accent }}>{ev.nama}</p>}
                        {ev.tanggal && <p className="mt-1 text-[10px] text-white/75">{formatDate(ev.tanggal)}{ev.jam ? ` · ${ev.jam} WIB` : ""}</p>}
                        {ev.tempat && <p className="mt-1 text-base" style={{ fontFamily: font }}>{ev.tempat}</p>}
                        {ev.dresscode && <p className="mt-0.5 text-[8px] uppercase tracking-wider text-white/60">Dress Code: {ev.dresscode}</p>}
                        {ev.alamat && <p className="mt-0.5 text-[9px] text-white/60">{ev.alamat}</p>}
                        {ev.maps_url && <span className="mt-1.5 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[8px]" style={{ borderColor: `${accent}66` }}><ExternalLink className="h-2.5 w-2.5" /> Peta</span>}
                      </div>
                    ) : null
                  ))}
                </div>

                {/* KISAH */}
                {design.kisah.enabled && design.kisah.items.some((it) => it.judul || it.cerita || it.tahun) && (
                  <div className="mt-5 w-full">
                    <p className="mb-2 text-[8px] uppercase tracking-[0.3em] text-white/60">Kisah Kami</p>
                    <div className="space-y-2 text-left">
                      {design.kisah.items.filter((it) => it.judul || it.cerita || it.tahun).slice(0, 3).map((it, i) => (
                        <div key={i} className="rounded-md border-l-2 bg-white/5 p-2" style={{ borderLeftColor: accent }}>
                          {it.tahun && <p className="text-[7px] uppercase tracking-wider" style={{ color: accent }}>{it.tahun}</p>}
                          {it.judul && <p className="text-[10px]" style={{ fontFamily: font }}>{it.judul}</p>}
                          {it.cerita && <p className="mt-0.5 text-[8px] text-white/70">{it.cerita}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {layout === "bottom" && s.couple_photo && <div className="mt-6"><PhotoBlock src={s.couple_photo} shape="bottom" /></div>}

                {/* GALERI */}
                {design.galeri.enabled && gallery.length > 0 && (
                  <div className="mt-6 w-full">
                    <p className="mb-2 text-[8px] uppercase tracking-[0.3em] text-white/60">Galeri</p>
                    <div className="columns-2 gap-1.5 [&>*]:mb-1.5">
                      {gallery.map((g, i) => (
                        <img key={i} src={g} alt={`g-${i}`} className="w-full break-inside-avoid rounded-md border border-white/10 object-cover" />
                      ))}
                    </div>
                    {design.galeri.quote && <p className="mt-2 text-[9px] italic text-white/70" style={{ fontFamily: font }}>"{design.galeri.quote}"</p>}
                  </div>
                )}

                {/* GIFT */}
                {design.gift.enabled && design.gift.accounts.some((a) => a.nomor || a.label) && (
                  <div className="mt-5 w-full">
                    <p className="mb-2 text-[8px] uppercase tracking-[0.3em] text-white/60">Wedding Gift</p>
                    <div className="space-y-1.5">
                      {design.gift.accounts.filter((a) => a.nomor || a.label).slice(0, 3).map((a, i) => (
                        <div key={i} className="rounded-md border border-white/10 bg-white/5 p-2 text-left">
                          {a.label && <p className="text-[8px] uppercase tracking-wider" style={{ color: accent }}>{a.label}</p>}
                          {a.nomor && <p className="text-[10px] tabular-nums text-white/85">{a.nomor}</p>}
                          {a.atas_nama && <p className="text-[8px] text-white/60">a.n. {a.atas_nama}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* PENUTUP */}
                {design.penutup.enabled && (design.penutup.pesan || design.penutup.judul) && (
                  <div className="mt-5 w-full rounded-lg border border-white/10 bg-white/5 p-3 backdrop-blur-md">
                    {design.penutup.judul && <p className="text-[11px]" style={{ fontFamily: font, color: accent }}>{design.penutup.judul}</p>}
                    {design.penutup.pesan && <p className="mt-1 text-[9px] text-white/75">{design.penutup.pesan}</p>}
                    {(design.penutup.ttd_pria || design.penutup.ttd_wanita) && (
                      <div className="mt-2 grid grid-cols-2 gap-1 text-[8px] text-white/70">
                        {design.penutup.ttd_pria && <p>Klg. Pria<br /><span style={{ color: accent }}>{design.penutup.ttd_pria}</span></p>}
                        {design.penutup.ttd_wanita && <p>Klg. Wanita<br /><span style={{ color: accent }}>{design.penutup.ttd_wanita}</span></p>}
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-8 flex items-center gap-1 text-[8px] text-white/50">
                  build with love <Heart className="h-2.5 w-2.5" style={{ color: accent, fill: accent }} /> · Wedifylab
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <p className="mt-3 text-center text-xs text-muted-foreground">Perubahan tampil langsung. Klik "Simpan" agar tersimpan permanen.</p>
    </div>
  );
}
